import React from 'react'
import { Volume2, Clock, Scissors, Play, Pause, Music, RotateCcw, Sliders, Zap, Gauge, GaugeIcon } from 'lucide-react'
import MediaPreview from './MediaPreview'

/**
 * Audio Element Properties Component
 * Handles the properties panel for audio elements
 */
const AudioElementProperties = ({
  element,
  selectedElement,
  updateElementProperty,
  currentPage,
  templateType
}) => {
  if (!element || element.type !== 'audio') {
    return null
  }

  // Format time for display
  const formatTime = (seconds) => {
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`
    } else {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = (seconds % 60).toFixed(1)
      return `${minutes}m ${remainingSeconds}s`
    }
  }

  // Get timeline information
  const getTimelineInfo = () => {
    if (!element.time) return null

    const startTime = element.time.start || 0
    const duration = element.time.duration || 0
    const endTime = startTime + duration

    return {
      start: startTime,
      duration: duration,
      end: endTime
    }
  }

  const timelineInfo = getTimelineInfo()
  
  // Get audio file name and duration
  const audioFileName = element.name || element.file?.name || 'Untitled Audio'
  const audioDuration = element.file?.duration || element.duration || 0
  
  // Get the correct element ID (use element.id if selectedElement is null, e.g., for audio tracks)
  const elementId = selectedElement || element.id
  
  // Reset duration to original file duration
  const handleResetDuration = () => {
    if (audioDuration > 0) {
      if (element.time) {
        updateElementProperty(elementId, 'time', {
          ...element.time,
          duration: audioDuration
        })
      } else {
        updateElementProperty(elementId, 'duration', audioDuration)
      }
    }
  }
  
  // Reset trim settings to use full audio file
  const handleResetTrim = () => {
    updateElementProperty(elementId, 'trim', {
      start: 0,
      end: audioDuration > 0 ? audioDuration : null
    })
  }
  
  // Validate trim settings
  const validateTrim = (trimStart, trimEnd) => {
    if (trimStart < 0) return { valid: false, error: 'Trim Start cannot be negative' }
    if (trimEnd !== null && trimEnd <= trimStart) return { valid: false, error: 'Trim End must be greater than Trim Start' }
    if (audioDuration > 0 && trimEnd !== null && trimEnd > audioDuration) return { valid: false, error: `Trim End cannot exceed audio duration (${formatTime(audioDuration)})` }
    if (audioDuration > 0 && trimStart > audioDuration) return { valid: false, error: `Trim Start cannot exceed audio duration (${formatTime(audioDuration)})` }
    return { valid: true }
  }

  return (
    <div className="space-y-4">
      {/* Inline media preview (play / fullsize) */}
      <MediaPreview element={element} />

      {/* Audio File Information - At the top */}
      <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
        <div className="flex items-center space-x-2 mb-3">
          <Music className="w-4 h-4 text-blue-600" />
          <h3 className="text-sm font-medium text-blue-700">Audio File</h3>
        </div>
        
        <div className="space-y-2">
          <div>
            <label className="text-xs text-blue-600 font-medium block mb-1">Name</label>
            <div className="text-sm text-blue-900 font-semibold break-words">{audioFileName}</div>
          </div>
          
          {audioDuration > 0 && (
            <div>
              <label className="text-xs text-blue-600 font-medium block mb-1">Duration</label>
              <div className="text-sm text-blue-900 font-semibold">{formatTime(audioDuration)}</div>
            </div>
          )}
        </div>
      </div>

      {/* Audio Settings */}
      <div className="bg-green-50 p-3 rounded-lg border border-green-200">
        <div className="flex items-center space-x-2 mb-3">
          <Volume2 className="w-4 h-4 text-green-600" />
          <h3 className="text-sm font-medium text-green-700">Audio Settings</h3>
        </div>
        
        <div className="space-y-3">
          {/* Volume */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-green-600 font-medium">Volume</label>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={element.settings?.volume !== undefined ? element.settings.volume : 1}
                onChange={(e) => updateElementProperty(elementId, 'settings', {
                  ...(element.settings || {}),
                  volume: parseFloat(e.target.value)
                })}
                className="w-24"
              />
              <span className="text-xs text-green-500 min-w-[40px] text-right">
                {Math.round((element.settings?.volume !== undefined ? element.settings.volume : 1) * 100)}%
              </span>
            </div>
          </div>

          {/* Fade In */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-green-600 font-medium">Fade In</label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="0"
                step="0.1"
                value={element.settings?.fadeIn || 0}
                onChange={(e) => updateElementProperty(elementId, 'settings', {
                  ...(element.settings || {}),
                  fadeIn: parseFloat(e.target.value) || 0
                })}
                className="w-16 px-2 py-1 text-sm border border-green-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <span className="text-xs text-green-500">s</span>
            </div>
          </div>

          {/* Fade Out */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-green-600 font-medium">Fade Out</label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="0"
                step="0.1"
                value={element.settings?.fadeOut || 0}
                onChange={(e) => updateElementProperty(elementId, 'settings', {
                  ...(element.settings || {}),
                  fadeOut: parseFloat(e.target.value) || 0
                })}
                className="w-16 px-2 py-1 text-sm border border-green-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <span className="text-xs text-green-500">s</span>
            </div>
          </div>

          {/* Pan */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-green-600 font-medium">Pan</label>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="-1"
                max="1"
                step="0.1"
                value={element.settings?.pan || 0}
                onChange={(e) => updateElementProperty(elementId, 'settings', {
                  ...(element.settings || {}),
                  pan: parseFloat(e.target.value)
                })}
                className="w-24"
              />
              <span className="text-xs text-green-500 min-w-[35px]">
                {element.settings?.pan > 0 ? `R${Math.round(element.settings.pan * 100)}` : 
                 element.settings?.pan < 0 ? `L${Math.round(Math.abs(element.settings.pan) * 100)}` : 'C'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Trim Settings */}
      <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Scissors className="w-4 h-4 text-orange-600" />
            <h3 className="text-sm font-medium text-orange-700">Trim Settings</h3>
          </div>
          {element.trim && (
            <button
              onClick={handleResetTrim}
              className="text-xs text-orange-600 hover:text-orange-800 px-2 py-1 rounded hover:bg-orange-100 transition-colors"
              title="Reset trim to use full audio"
            >
              Reset
            </button>
          )}
        </div>
        
        <div className="space-y-2">
          {/* Visual indicator of trimmed portion */}
          {audioDuration > 0 && element.trim && (
            <div className="mb-3">
              <div className="text-xs text-orange-600 mb-1">Audio Source</div>
              <div className="relative h-2 bg-orange-200 rounded-full overflow-hidden">
                <div 
                  className="absolute h-full bg-orange-500"
                  style={{
                    left: `${((element.trim.start || 0) / audioDuration) * 100}%`,
                    width: `${((element.trim.end || audioDuration) - (element.trim.start || 0)) / audioDuration * 100}%`
                  }}
                  title={`Trimmed: ${formatTime(element.trim.start || 0)} - ${formatTime(element.trim.end || audioDuration)}`}
                />
              </div>
              <div className="flex justify-between text-xs text-orange-500 mt-1">
                <span>0s</span>
                <span>{formatTime(audioDuration)}</span>
              </div>
            </div>
          )}
          
          {/* Trim Start */}
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm text-orange-600 font-medium">Trim Start</label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="0"
                max={audioDuration > 0 ? audioDuration : undefined}
                step="0.1"
                value={element.trim?.start || 0}
                onChange={(e) => {
                  const newStart = parseFloat(e.target.value) || 0
                  const validation = validateTrim(newStart, element.trim?.end)
                  if (validation.valid) {
                    const newEnd = element.trim?.end || (audioDuration > 0 ? audioDuration : 0)
                    updateElementProperty(elementId, 'trim', {
                      ...(element.trim || {}),
                      start: newStart
                    })
                    const trimmedDuration = (newEnd > 0 ? newEnd : audioDuration) - newStart
                    if (trimmedDuration > 0) {
                      updateElementProperty(elementId, 'time', {
                        ...(element.time || {}),
                        duration: trimmedDuration
                      })
                    }
                  } else {
                    // Show error but still allow input (user might be typing)
                    console.warn(validation.error)
                  }
                }}
                className="w-16 px-2 py-1 text-sm border border-orange-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <span className="text-xs text-orange-500">s</span>
            </div>
          </div>

          {/* Trim End */}
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm text-orange-600 font-medium">Trim End</label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min={element.trim?.start || 0}
                max={audioDuration > 0 ? audioDuration : undefined}
                step="0.1"
                value={element.trim?.end || (audioDuration > 0 ? audioDuration : 0)}
                onChange={(e) => {
                  const newEnd = parseFloat(e.target.value) || 0
                  const validation = validateTrim(element.trim?.start || 0, newEnd)
                  if (validation.valid) {
                    const newStart = element.trim?.start || 0
                    updateElementProperty(elementId, 'trim', {
                      ...(element.trim || {}),
                      end: newEnd > 0 ? newEnd : null
                    })
                    const effectiveEnd = newEnd > 0 ? newEnd : audioDuration
                    const trimmedDuration = effectiveEnd - newStart
                    if (trimmedDuration > 0) {
                      updateElementProperty(elementId, 'time', {
                        ...(element.time || {}),
                        duration: trimmedDuration
                      })
                    }
                  } else {
                    console.warn(validation.error)
                  }
                }}
                className="w-16 px-2 py-1 text-sm border border-orange-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <span className="text-xs text-orange-500">s</span>
            </div>
          </div>
          
          {/* Trimmed duration display */}
          {element.trim && (
            <div className="pt-2 border-t border-orange-200">
              <div className="flex items-center justify-between text-xs">
                <span className="text-orange-600">Trimmed Duration</span>
                <span className="text-orange-700 font-medium">
                  {formatTime((element.trim.end || audioDuration) - (element.trim.start || 0))}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Timeline Settings - Only for video templates */}
      {(templateType === 'video' || element.time !== undefined) && (
      <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
        <div className="flex items-center space-x-2 mb-3">
          <Clock className="w-4 h-4 text-purple-600" />
          <h3 className="text-sm font-medium text-purple-700">Timeline</h3>
        </div>
        
        <div className="space-y-3">
          {/* Start Time */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-purple-600 font-medium">Start Time</label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="0"
                step="0.1"
                value={element.time?.start !== undefined ? element.time.start : (element.start || 0)}
                onChange={(e) => {
                  const newStart = parseFloat(e.target.value) || 0
                  if (element.time) {
                    updateElementProperty(elementId, 'time', {
                      ...element.time,
                      start: newStart
                    })
                  } else {
                    updateElementProperty(elementId, 'start', newStart)
                  }
                }}
                className="w-16 px-2 py-1 text-sm border border-purple-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <span className="text-xs text-purple-500">s</span>
            </div>
          </div>

          {/* Duration */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-purple-600 font-medium">Duration</label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={element.time?.duration !== undefined ? element.time.duration : (element.duration || 0)}
                onChange={(e) => {
                  const newDuration = parseFloat(e.target.value) || 0.1
                  if (element.time) {
                    updateElementProperty(elementId, 'time', {
                      ...element.time,
                      duration: newDuration
                    })
                  } else {
                    updateElementProperty(elementId, 'duration', newDuration)
                  }
                }}
                className="w-16 px-2 py-1 text-sm border border-purple-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <span className="text-xs text-purple-500">s</span>
              {audioDuration > 0 && (
                <button
                  onClick={handleResetDuration}
                  className="ml-2 p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-100 rounded transition-colors"
                  title="Reset to original duration"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {timelineInfo && (
            <div className="pt-2 border-t border-purple-200">
              <div className="flex items-center justify-between text-xs">
                <span className="text-purple-600">End Time</span>
                <span className="text-purple-700 font-medium">{formatTime(timelineInfo.end)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* EQ (Equalization) */}
      <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Sliders className="w-4 h-4 text-indigo-600" />
            <h3 className="text-sm font-medium text-indigo-700">EQ (Equalization)</h3>
          </div>
          {(element.settings?.audioEffects?.eq?.low !== 0 || 
            element.settings?.audioEffects?.eq?.mid !== 0 || 
            element.settings?.audioEffects?.eq?.high !== 0) && (
            <button
              onClick={() => updateElementProperty(elementId, 'settings', {
                ...(element.settings || {}),
                audioEffects: {
                  ...(element.settings?.audioEffects || {}),
                  eq: { low: 0, mid: 0, high: 0 }
                }
              })}
              className="text-xs text-indigo-600 hover:text-indigo-800 px-2 py-1 rounded hover:bg-indigo-100 transition-colors"
              title="Reset EQ"
            >
              Reset
            </button>
          )}
        </div>
        
        <div className="space-y-3">
          {/* Low (Bass) */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-indigo-600 font-medium">Low (Bass)</label>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="-20"
                max="20"
                step="1"
                value={element.settings?.audioEffects?.eq?.low || 0}
                onChange={(e) => updateElementProperty(elementId, 'settings', {
                  ...(element.settings || {}),
                  audioEffects: {
                    ...(element.settings?.audioEffects || {}),
                    eq: {
                      ...(element.settings?.audioEffects?.eq || {}),
                      low: parseFloat(e.target.value)
                    }
                  }
                })}
                className="w-24"
              />
              <span className="text-xs text-indigo-500 min-w-[35px] text-right">
                {element.settings?.audioEffects?.eq?.low > 0 ? '+' : ''}{element.settings?.audioEffects?.eq?.low || 0}dB
              </span>
            </div>
          </div>

          {/* Mid */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-indigo-600 font-medium">Mid</label>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="-20"
                max="20"
                step="1"
                value={element.settings?.audioEffects?.eq?.mid || 0}
                onChange={(e) => updateElementProperty(elementId, 'settings', {
                  ...(element.settings || {}),
                  audioEffects: {
                    ...(element.settings?.audioEffects || {}),
                    eq: {
                      ...(element.settings?.audioEffects?.eq || {}),
                      mid: parseFloat(e.target.value)
                    }
                  }
                })}
                className="w-24"
              />
              <span className="text-xs text-indigo-500 min-w-[35px] text-right">
                {element.settings?.audioEffects?.eq?.mid > 0 ? '+' : ''}{element.settings?.audioEffects?.eq?.mid || 0}dB
              </span>
            </div>
          </div>

          {/* High (Treble) */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-indigo-600 font-medium">High (Treble)</label>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="-20"
                max="20"
                step="1"
                value={element.settings?.audioEffects?.eq?.high || 0}
                onChange={(e) => updateElementProperty(elementId, 'settings', {
                  ...(element.settings || {}),
                  audioEffects: {
                    ...(element.settings?.audioEffects || {}),
                    eq: {
                      ...(element.settings?.audioEffects?.eq || {}),
                      high: parseFloat(e.target.value)
                    }
                  }
                })}
                className="w-24"
              />
              <span className="text-xs text-indigo-500 min-w-[35px] text-right">
                {element.settings?.audioEffects?.eq?.high > 0 ? '+' : ''}{element.settings?.audioEffects?.eq?.high || 0}dB
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Compression */}
      <div className="bg-pink-50 p-3 rounded-lg border border-pink-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Zap className="w-4 h-4 text-pink-600" />
            <h3 className="text-sm font-medium text-pink-700">Compression</h3>
          </div>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={element.settings?.audioEffects?.compression?.enabled || false}
              onChange={(e) => updateElementProperty(elementId, 'settings', {
                ...(element.settings || {}),
                audioEffects: {
                  ...(element.settings?.audioEffects || {}),
                  compression: {
                    ...(element.settings?.audioEffects?.compression || {
                      threshold: -12,
                      ratio: 4,
                      attack: 5,
                      release: 50
                    }),
                    enabled: e.target.checked
                  }
                }
              })}
              className="w-4 h-4"
            />
            <span className="text-xs text-pink-600">Enable</span>
          </label>
        </div>
        
        {element.settings?.audioEffects?.compression?.enabled && (
          <div className="space-y-3">
            {/* Threshold */}
            <div className="flex items-center justify-between">
              <label className="text-sm text-pink-600 font-medium">Threshold</label>
              <div className="flex items-center space-x-2">
                <input
                  type="range"
                  min="-60"
                  max="0"
                  step="1"
                  value={element.settings?.audioEffects?.compression?.threshold || -12}
                  onChange={(e) => updateElementProperty(elementId, 'settings', {
                    ...(element.settings || {}),
                    audioEffects: {
                      ...(element.settings?.audioEffects || {}),
                      compression: {
                        ...(element.settings?.audioEffects?.compression || {}),
                        threshold: parseFloat(e.target.value)
                      }
                    }
                  })}
                  className="w-24"
                />
                <span className="text-xs text-pink-500 min-w-[40px] text-right">
                  {element.settings?.audioEffects?.compression?.threshold || -12}dB
                </span>
              </div>
            </div>

            {/* Ratio */}
            <div className="flex items-center justify-between">
              <label className="text-sm text-pink-600 font-medium">Ratio</label>
              <div className="flex items-center space-x-2">
                <input
                  type="range"
                  min="1"
                  max="20"
                  step="1"
                  value={element.settings?.audioEffects?.compression?.ratio || 4}
                  onChange={(e) => updateElementProperty(elementId, 'settings', {
                    ...(element.settings || {}),
                    audioEffects: {
                      ...(element.settings?.audioEffects || {}),
                      compression: {
                        ...(element.settings?.audioEffects?.compression || {}),
                        ratio: parseFloat(e.target.value)
                      }
                    }
                  })}
                  className="w-24"
                />
                <span className="text-xs text-pink-500 min-w-[40px] text-right">
                  {element.settings?.audioEffects?.compression?.ratio || 4}:1
                </span>
              </div>
            </div>

            {/* Attack */}
            <div className="flex items-center justify-between">
              <label className="text-sm text-pink-600 font-medium">Attack</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="0.1"
                  max="100"
                  step="0.1"
                  value={element.settings?.audioEffects?.compression?.attack || 5}
                  onChange={(e) => updateElementProperty(elementId, 'settings', {
                    ...(element.settings || {}),
                    audioEffects: {
                      ...(element.settings?.audioEffects || {}),
                      compression: {
                        ...(element.settings?.audioEffects?.compression || {}),
                        attack: parseFloat(e.target.value) || 5
                      }
                    }
                  })}
                  className="w-16 px-2 py-1 text-sm border border-pink-300 rounded focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
                <span className="text-xs text-pink-500">ms</span>
              </div>
            </div>

            {/* Release */}
            <div className="flex items-center justify-between">
              <label className="text-sm text-pink-600 font-medium">Release</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="1"
                  max="1000"
                  step="1"
                  value={element.settings?.audioEffects?.compression?.release || 50}
                  onChange={(e) => updateElementProperty(elementId, 'settings', {
                    ...(element.settings || {}),
                    audioEffects: {
                      ...(element.settings?.audioEffects || {}),
                      compression: {
                        ...(element.settings?.audioEffects?.compression || {}),
                        release: parseFloat(e.target.value) || 50
                      }
                    }
                  })}
                  className="w-16 px-2 py-1 text-sm border border-pink-300 rounded focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
                <span className="text-xs text-pink-500">ms</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Normalization */}
      <div className="bg-cyan-50 p-3 rounded-lg border border-cyan-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Gauge className="w-4 h-4 text-cyan-600" />
            <h3 className="text-sm font-medium text-cyan-700">Normalization</h3>
          </div>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={element.settings?.audioEffects?.normalization?.enabled || false}
              onChange={(e) => updateElementProperty(elementId, 'settings', {
                ...(element.settings || {}),
                audioEffects: {
                  ...(element.settings?.audioEffects || {}),
                  normalization: {
                    ...(element.settings?.audioEffects?.normalization || {
                      targetLevel: -3
                    }),
                    enabled: e.target.checked
                  }
                }
              })}
              className="w-4 h-4"
            />
            <span className="text-xs text-cyan-600">Enable</span>
          </label>
        </div>
        
        {element.settings?.audioEffects?.normalization?.enabled && (
          <div className="space-y-3">
            {/* Target Level */}
            <div className="flex items-center justify-between">
              <label className="text-sm text-cyan-600 font-medium">Target Level</label>
              <div className="flex items-center space-x-2">
                <input
                  type="range"
                  min="-30"
                  max="0"
                  step="1"
                  value={element.settings?.audioEffects?.normalization?.targetLevel || -3}
                  onChange={(e) => updateElementProperty(elementId, 'settings', {
                    ...(element.settings || {}),
                    audioEffects: {
                      ...(element.settings?.audioEffects || {}),
                      normalization: {
                        ...(element.settings?.audioEffects?.normalization || {}),
                        targetLevel: parseFloat(e.target.value)
                      }
                    }
                  })}
                  className="w-24"
                />
                <span className="text-xs text-cyan-500 min-w-[40px] text-right">
                  {element.settings?.audioEffects?.normalization?.targetLevel || -3}dB
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Speed/Pitch Control */}
      <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
        <div className="flex items-center space-x-2 mb-3">
          <GaugeIcon className="w-4 h-4 text-yellow-600" />
          <h3 className="text-sm font-medium text-yellow-700">Speed & Pitch</h3>
        </div>
        
        <div className="space-y-3">
          {/* Speed */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-yellow-600 font-medium">Speed</label>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="0.25"
                max="4.0"
                step="0.05"
                value={element.settings?.audioEffects?.speed?.value || 1.0}
                onChange={(e) => updateElementProperty(elementId, 'settings', {
                  ...(element.settings || {}),
                  audioEffects: {
                    ...(element.settings?.audioEffects || {}),
                    speed: {
                      ...(element.settings?.audioEffects?.speed || {}),
                      value: parseFloat(e.target.value)
                    }
                  }
                })}
                className="w-24"
              />
              <span className="text-xs text-yellow-500 min-w-[40px] text-right">
                {((element.settings?.audioEffects?.speed?.value || 1.0) * 100).toFixed(0)}%
              </span>
            </div>
          </div>

          {/* Maintain Pitch */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-yellow-600 font-medium">Maintain Pitch</label>
            <input
              type="checkbox"
              checked={element.settings?.audioEffects?.speed?.maintainPitch !== false}
              onChange={(e) => updateElementProperty(elementId, 'settings', {
                ...(element.settings || {}),
                audioEffects: {
                  ...(element.settings?.audioEffects || {}),
                  speed: {
                    ...(element.settings?.audioEffects?.speed || {}),
                    maintainPitch: e.target.checked
                  }
                }
              })}
              className="w-4 h-4"
            />
          </div>

          {/* Pitch */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-yellow-600 font-medium">Pitch</label>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="-12"
                max="12"
                step="1"
                value={element.settings?.audioEffects?.pitch?.semitones || 0}
                onChange={(e) => updateElementProperty(elementId, 'settings', {
                  ...(element.settings || {}),
                  audioEffects: {
                    ...(element.settings?.audioEffects || {}),
                    pitch: {
                      ...(element.settings?.audioEffects?.pitch || {}),
                      semitones: parseFloat(e.target.value)
                    }
                  }
                })}
                className="w-24"
              />
              <span className="text-xs text-yellow-500 min-w-[40px] text-right">
                {element.settings?.audioEffects?.pitch?.semitones > 0 ? '+' : ''}{element.settings?.audioEffects?.pitch?.semitones || 0}st
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Track Information */}
      <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
        <div className="flex items-center space-x-2 mb-3">
          <Music className="w-4 h-4 text-gray-600" />
          <h3 className="text-sm font-medium text-gray-700">Track Information</h3>
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 font-medium">Layer</label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="0"
                step="1"
                value={(element.trackId || 0) + 1}
                onChange={(e) => {
                  const display = parseInt(e.target.value, 10)
                  if (Number.isNaN(display)) return
                  const newTrackId = Math.max(0, display - 1)
                  updateElementProperty(elementId, 'trackId', newTrackId)
                }}
                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                title="Layer number shown as 'Audio X' on the timeline"
              />
              <span className="text-xs text-gray-500 min-w-[60px]">Audio {(element.trackId || 0) + 1}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AudioElementProperties
